import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { applyLoginSession } from "../authApi";
import { isSupabaseAuthConfigured, supabase } from "../lib/supabaseClient";

function sanitizeNextPath(value) {
  const requested = String(value || "/dashboard").trim();
  if (!requested.startsWith("/")) return "/dashboard";
  if (requested === "/" || requested.startsWith("/login") || requested.startsWith("/auth/callback")) {
    return "/dashboard";
  }
  return requested;
}

function readHashSession(hashValue) {
  const hash = String(hashValue || "").replace(/^#/, "");
  if (!hash) return null;

  const hashParams = new URLSearchParams(hash);
  const accessToken = String(hashParams.get("access_token") || "").trim();
  const refreshToken = String(hashParams.get("refresh_token") || "").trim();

  if (!accessToken) return null;

  return {
    accessToken,
    refreshToken: refreshToken || null
  };
}

export default function OAuthBootstrap() {
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    async function finalizeIfOAuthReturn() {
      if (!isSupabaseAuthConfigured || !supabase) {
        return;
      }

      const searchParams = new URLSearchParams(window.location.search || "");
      const hasCode = Boolean(searchParams.get("code"));
      const hashSession = readHashSession(window.location.hash);
      const hasOauthError = Boolean(searchParams.get("error") || searchParams.get("error_description"));

      if (!hasCode && !hashSession && !hasOauthError) {
        return;
      }

      const nextPath = sanitizeNextPath(searchParams.get("next"));

      try {
        if (hasCode) {
          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(
            String(searchParams.get("code") || "")
          );
          if (exchangeError) {
            throw exchangeError;
          }
        }

        if (hashSession?.accessToken) {
          if (hashSession.refreshToken) {
            const { error: setSessionError } = await supabase.auth.setSession({
              access_token: hashSession.accessToken,
              refresh_token: hashSession.refreshToken
            });

            if (setSessionError) {
              throw setSessionError;
            }
          }

          applyLoginSession({
            accessToken: hashSession.accessToken,
            refreshToken: hashSession.refreshToken,
            persist: true
          });
        }

        const { data, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          throw sessionError;
        }

        const accessToken = String(data?.session?.access_token || "").trim();
        if (!accessToken) {
          throw new Error("Unable to establish a session from Google sign-in.");
        }

        applyLoginSession({
          accessToken,
          refreshToken: data?.session?.refresh_token || null,
          persist: true
        });

        if (!cancelled) {
          navigate(nextPath, { replace: true });
        }
      } catch {
        if (!cancelled) {
          navigate("/login", { replace: true });
        }
      }
    }

    finalizeIfOAuthReturn();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return null;
}
